import { Modal } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { generatePath, useMatch, useNavigate } from "react-router";
import { useCanAssessManagerSiteFinancially } from "../../../hooks/useCanAssessManagerSiteFinanciallyQuery";
import { useGeneratorSingleEndpoint } from "../../../hooks/useGeneratorSingleEndpoint";
import { useGeneratorSingleEndpointMutations } from "../../../hooks/useGeneratorSingleEndpointMutation";
import { usePersons } from "../../../hooks/usePersons";
import { useSiteCategories } from "../../../hooks/useSiteCategories";
import { useToast } from "../../../mantine/notifications/useToast";
import { ROUTES } from "../../../routes";
import { getNeedsJahrDesGebotstermins } from "../../../utils/getNeedsJahrDesGebotstermins";
import { isPremiumSite } from "../../../utils/isPremiumSite";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { isVeraeusserungsformenReady } from "./GeneratorModal.constants";
import type { GeneratorSingleEndpointResponse } from "./GeneratorModal.types";
import { GeneratorModalForm } from "./GeneratorModalForm";

interface GeneratorModalProps {
  afterSave?: () => void;
  generatorId?: number;
  variantId: number;
}

function GeneratorModal({
  afterSave,
  generatorId: generatorIdArg,
  variantId
}: GeneratorModalProps) {
  const queryClient = useQueryClient();
  const { showToast, showErrorToast } = useToast();
  const match = useMatch(`${ROUTES.site.generator}/*`);
  const isGeneratorRoute = match !== null;

  const siteIdParam = match?.params?.siteId;
  const generatorIdParam = match?.params?.componentId;
  const generatorId =
    generatorIdArg || (generatorIdParam ? Number(generatorIdParam) : undefined);
  const siteId = Number(siteIdParam) || undefined;
  const { persons } = usePersons({
    variantId,
    queryOptions: { enabled: isGeneratorRoute }
  });
  const {
    data: generator,
    isLoading,
    error,
    isPlaceholderData
  } = useGeneratorSingleEndpoint(
    generatorId,
    { enabled: isGeneratorRoute },
    siteId
  );
  const { editGeneratorSingleEndpoint } =
    useGeneratorSingleEndpointMutations(siteId);
  const isPending = editGeneratorSingleEndpoint.isPending;

  const {
    siteCategories,
    isLoading: isLoadingSiteCategories,
    error: siteCategoriesError
  } = useSiteCategories(siteId);
  const { currentUserCanAssessManagerSiteFinancially } =
    useCanAssessManagerSiteFinancially();
  const generatorForm = useForm<GeneratorSingleEndpointResponse>({
    values: generator
  });
  const { reset } = generatorForm;
  const navigate = useNavigate();

  const isPremium = isPremiumSite(siteCategories, siteId);

  const showPriceData = !!currentUserCanAssessManagerSiteFinancially;
  const isCommissioningNotRequired = generator?.commissioning === null; // if there was a value before, there still needs to be a value

  const { setError, watch } = generatorForm;

  const parentObjectPath: string =
    match?.params.siteId && match?.params.projectId
      ? generatePath(ROUTES.site.generators, {
          projectId: match?.params.projectId,
          siteId: match?.params.siteId
        })
      : "./";

  function handleClose() {
    queryClient.invalidateQueries({
      queryKey: ["generatorSingleEndpoint", { generatorId }]
    });
    navigate(parentObjectPath);
    reset();
  }

  const onSubmit = (data: GeneratorSingleEndpointResponse) => {
    let sanitizedData = {
      ...data
    };
    // make sure to send true null values for empty fields
    if (sanitizedData.einheit && !sanitizedData.einheit.sr_code) {
      sanitizedData.einheit.sr_code = null;
    }

    if (sanitizedData.einheit?.eeg_anlage) {
      // backend doesn't allow empty strings
      sanitizedData.einheit.eeg_anlage.anlagenschluessel_eeg =
        sanitizedData.einheit.eeg_anlage.anlagenschluessel_eeg?.trim() || null;

      if ("jahr_des_gebotstermins" in sanitizedData.einheit.eeg_anlage) {
        const foerderregime = sanitizedData.einheit.eeg_anlage.foerderregime;
        const needsJahrDesGebotstermins =
          !!foerderregime && getNeedsJahrDesGebotstermins(foerderregime);
        sanitizedData.einheit.eeg_anlage.jahr_des_gebotstermins =
          needsJahrDesGebotstermins
            ? sanitizedData.einheit.eeg_anlage.jahr_des_gebotstermins
            : null;
      }

      if (sanitizedData.einheit.eeg_anlage.veraeusserungsformen) {
        sanitizedData = {
          ...sanitizedData,
          einheit: {
            ...sanitizedData.einheit,
            eeg_anlage: {
              ...sanitizedData.einheit.eeg_anlage,
              veraeusserungsformen:
                sanitizedData.einheit.eeg_anlage.veraeusserungsformen.filter(
                  (row) => row.veraeusserungsform
                )
            }
          }
        };
      }
    }

    // do not pass the Veraeusserungsformen of the generator
    // as long as it is disabled for the users and they cannot even see the data in the form to avoid potential errors.
    // Will be reactivated once the feature (WUP-8648) is ready to be used by the users
    // and we have finally migrated the Veraeusserungsformen from the contract to the generator itself in the backend.
    if (!isVeraeusserungsformenReady && sanitizedData.einheit?.eeg_anlage) {
      const { veraeusserungsformen: _, ...eegAnlageRest } =
        sanitizedData.einheit.eeg_anlage;
      sanitizedData = {
        ...sanitizedData,
        einheit: {
          ...sanitizedData.einheit,
          eeg_anlage: { ...eegAnlageRest }
        }
      };
    }

    editGeneratorSingleEndpoint.mutate(sanitizedData, {
      onSuccess: () => {
        showToast({
          type: "success",
          message: `Die Änderungen am Erzeuger ${data.name} wurden gespeichert.`
        });
        afterSave?.();
        handleClose();
      },
      onError: (error) => {
        setErrorsFromResponseData<GeneratorSingleEndpointResponse>(
          showErrorToast,
          error,
          watch(),
          setError,
          t("errors.UnknownError")
        );
      }
    });
  };

  return (
    <Modal
      opened={isGeneratorRoute}
      size="xl"
      title={`${generator?.name ?? "Erzeuger"} bearbeiten`}
      onClose={handleClose}
    >
      <LoadOrError
        error={error || siteCategoriesError}
        loading={isLoading || isPlaceholderData || isLoadingSiteCategories}
      >
        {generatorId && generator && (
          <GeneratorModalForm
            generator={generator}
            generatorForm={generatorForm}
            generatorId={generatorId}
            isCommissioningNotRequired={isCommissioningNotRequired}
            isPending={isPending}
            isPremium={isPremium}
            persons={persons}
            showPriceData={showPriceData}
            siteId={siteId}
            variantId={variantId}
            onClose={handleClose}
            onSubmit={onSubmit}
          />
        )}
      </LoadOrError>
    </Modal>
  );
}

export { GeneratorModal };
